import { EGridLayout } from 'components/grid/constants';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router';
import { WellDisplayMode } from './constants';
import {
  useAllPlates,
  useAllRegions,
  useInspectionSetByGroupBatchId,
  useLastSelectedWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import { useRMDispatch } from 'redux/hooks';
import { useFetchFocusLevelData, useWellList } from './hooks';
import {
  DisplayModes,
  NavigationSource,
  SetDisplayMode,
  showAlertSnackBar,
} from 'utils/Global';
import WellProcessor from './WellProcessor';
import SlideShow from './slideShow/slideShow';
import { NavigatorMapDTO } from 'business/models/Models';
import { SizeDetails } from 'components/canvas/constants';
import { setDrawingTool, unSelectDrawing } from 'redux/reducers/wellReducer';
import { OverlayTools } from './drawingManager/constants';
import {
  setWellImagingSettingDetails,
  setWellNavigationDetails,
} from 'redux/reducers/experimentDataReducer';
import { WellDetailsStates } from 'business/models/CustomModels';
import { batch } from 'react-redux';
import { ExpDashboardContext } from 'components/main/ExperimentDashboardContext';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import EAlertType from 'enums/EAlertType';
import { getNavigatorImageKeysAsString } from 'components/commandpanel/Navigator/utils';
import useRMLite from 'CustomHooks/useRMLite';
import { getRegionIdCached } from './utils';

interface Props {
  sizeDetails: SizeDetails;
  setIsWellLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
  setZoomInvocker?: (onZoom: (zoom: number) => void) => void;
  gridLayout: EGridLayout;
  changeHeader: (header: string) => void;
  BottomMenu: React.ReactNode;
  startWellSlideShow: boolean;
  setStartWellSlideShow: React.Dispatch<React.SetStateAction<boolean>>;
  isSlideShowInitiatedFromCanvas: boolean;
  navigatorMap: NavigatorMapDTO;
  setWellRef?: (wellKey: string, ref: any) => void;
}

const wellDashboard = (props: Props) => {
  const {
    sizeDetails,
    setIsWellLoaded,
    setZoomInvocker,
    gridLayout,
    changeHeader,
    BottomMenu,
    startWellSlideShow,
    setStartWellSlideShow,
    isSlideShowInitiatedFromCanvas,
    navigatorMap,
    setWellRef,
  } = props;

  const params = useParams();
  const history = useHistory();
  const { id, plateID, wellNum, dropNumber, imageBatchId, regionName, cpID, wellMode } =
    params;
  const lastSelectedWell = useLastSelectedWellNavigationDetails();
  const inspectionSet = useInspectionSetByGroupBatchId(imageBatchId);
  const dispatch = useRMDispatch();

  const wellsToShow = useWellList(
    lastSelectedWell,
    wellMode as WellDisplayMode,
    gridLayout,
    sizeDetails,
  );
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const allPlates = useAllPlates();
  const allRegions = useAllRegions();
  const allPlatesIds = allPlates?.map((plate) => plate.plateId);
  const cpIds = useMemo(
    () => wellsToShow?.map((well) => well?.wellDetails?.cpID),
    [wellsToShow?.length, selectedDropImageParameters?.cpId],
  );
  const inspectionIds = useMemo(
    () =>
      selectedDropImageParameters?.inspection?.groupedInspections
        ?.filter((ins) => allPlatesIds?.includes(ins.plateId))
        .map((filteredIns) => filteredIns.imageBatchId),
    [selectedDropImageParameters],
  );
  const expDashboardContext = useContext(ExpDashboardContext);
  const isFirstRender = useRef(true);
  useFetchFocusLevelData(lastSelectedWell, selectedDropImageParameters);
  const { isCanvasInFocus: isFocus, setIsCanvasInFocus: setIsFocus } =
    expDashboardContext;
  const rmLite = useRMLite();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    if (
      cpID &&
      navigatorMap &&
      !navigatorMap.drciList.some(
        (drc) =>
          drc.drc.cpId === parseInt(cpID) &&
          drc.inspectedWells.some(
            (ins) => ins.inspectionId === parseInt(imageBatchId) && ins.wells?.length,
          ),
      ) &&
      isFirstRender.current &&
      from === NavigationSource.Explorer
    ) {
      isFirstRender.current = false;
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'Selected image settings are not available for the current well. Reverting to available settings.',
      );
      dispatch(
        setWellImagingSettingDetails([
          {
            cpId: navigatorMap.profileList[0].id,
            dropNumber: parseInt(dropNumber, 10),
            inspection: inspectionSet,
            regionName: regionName,
          },
        ]),
      );
      const _updatedUrl = `/exp/${id}/${plateID}/${wellNum}/${dropNumber}/${navigatorMap.profileList[0].id}/${regionName}/${imageBatchId}/${wellMode}`;
      history.push(_updatedUrl);
      return;
    }
  }, [
    navigatorMap,
    selectedDropImageParameters?.regionName,
    selectedDropImageParameters?.dropNumber,
    cpIds,
    inspectionIds,
  ]);

  const selectedWell = useMemo<WellDetailsStates | null>(() => {
    if (!lastSelectedWell || !selectedDropImageParameters) {
      return null;
    }

    const {
      wellNum: selWellNum,
      plateID: selPlateID,
      plateNum: selPlateNum,
    } = lastSelectedWell;

    const {
      dropNumber: selDropNumber,
      regionName: selRegionName,
      cpId: selCpID,
      inspection: selInspection,
    } = selectedDropImageParameters;

    const regionID = getRegionIdCached(
      selPlateID,
      selWellNum,
      selDropNumber,
      selRegionName,
    );

    const newImageBatchId = selInspection?.groupedInspections?.find(
      (gi) => gi.plateId === selPlateID,
    )?.imageBatchId;

    // If we're selecting the same drop again, re-focus it
    if (
      Number(imageBatchId) === newImageBatchId &&
      Number(wellNum) == selWellNum &&
      Number(plateID) == selPlateID &&
      Number(dropNumber) == selDropNumber &&
      regionName == selRegionName
    ) {
      setIsFocus(true);
    }

    return {
      wellNum: selWellNum,
      plateID: selPlateID,
      plateNum: selPlateNum,
      dropNumber: selDropNumber,
      regionName: selRegionName,
      cpID: selCpID,
      inspectionSet: selInspection,
      regionID,
      imageBatchId: newImageBatchId,
    };
  }, [lastSelectedWell, selectedDropImageParameters]);

  useEffect(() => {
    changeHeader('showWellImage');
    SetDisplayMode(DisplayModes.WELLIMAGE);
    return () => {
      dispatch(unSelectDrawing());
      dispatch(setDrawingTool(OverlayTools.POINTER));
    };
  }, []);

  useEffect(() => {
    if (inspectionSet && (!lastSelectedWell || !selectedDropImageParameters)) {
      const parsedPlateId = parseInt(plateID, 10);
      const parsedWellNum = parseInt(wellNum, 10);
      const parsedDropNumber = parseInt(dropNumber, 10);
      const plateNum = allPlates?.find(
        (plate) => plate.plateId === parsedPlateId,
      )?.plateNumber;
      batch(() => {
        dispatch(
          setWellNavigationDetails([
            { plateNum: plateNum, wellNum: parsedWellNum, plateID: parsedPlateId },
          ]),
        );
        dispatch(
          setWellImagingSettingDetails([
            {
              cpId: parseInt(cpID, 10),
              dropNumber: parsedDropNumber,
              inspection: inspectionSet,
              regionName: regionName,
            },
          ]),
        );
      });
    }
  }, [
    plateID,
    wellNum,
    dropNumber,
    imageBatchId,
    regionName,
    cpID,
    wellMode,
    inspectionSet,
    allRegions,
  ]);

  useEffect(() => {
    setIsFocus(true);
    batch(() => {
      dispatch(unSelectDrawing());
      dispatch(setDrawingTool(OverlayTools.POINTER));
    });
  }, [
    lastSelectedWell?.plateID,
    lastSelectedWell?.wellNum,
    selectedDropImageParameters?.dropNumber,
    selectedDropImageParameters?.regionName,
    selectedDropImageParameters?.cpId,
    selectedDropImageParameters?.inspection,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const current = expDashboardContext.canvasContainerRef.current;
      if (current) {
        setIsFocus(current?.contains(event.target));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
  }, [expDashboardContext.canvasContainerRef]);

  const wellKeyGenerator = (wellDetails: WellDetailsStates) => {
    const { cpID, dropNumber, inspectionSet, plateID, plateNum, regionName, wellNum } =
      wellDetails;
    return getNavigatorImageKeysAsString(
      { plateID, wellNum, plateNum },
      { cpId: cpID, dropNumber, regionName, inspection: inspectionSet },
    );
  };
  if (!lastSelectedWell) {
    return <></>;
  }

  if (rmLite && wellMode === WellDisplayMode.Well) {
    const redirectTo = `/exp/${id}/${plateID}/${wellNum}/${dropNumber}/${cpID}/${regionName}/${imageBatchId}/${WellDisplayMode.Drop}`;
    history.push(redirectTo);
    return <></>;
  }

  return (
    <>
      {startWellSlideShow ? (
        <SlideShow
          showModal={startWellSlideShow}
          setShowModal={setStartWellSlideShow}
          isSlideShowInitiatedFromCanvas={isSlideShowInitiatedFromCanvas}
          displayMode={wellMode}
          gridLayout={gridLayout}
          imagedWells={expDashboardContext.imagedWells}
          isFocus={isFocus}
          wellKeyGenerator={wellKeyGenerator}
          setWellRef={setWellRef}
        />
      ) : (
        <div id="welldashboard-main-container" className="h-full flex flex-col">
          <div
            id="welldashboard-canvas-container"
            ref={expDashboardContext.canvasContainerRef}
            className={`h-[${window.innerHeight < 700 ? 92 : 94}%]`}
          >
            <WellProcessor
              wells={wellsToShow}
              displayMode={wellMode}
              gridLayout={gridLayout}
              setZoomInvocker={setZoomInvocker}
              setIsWellLoaded={setIsWellLoaded}
              selectedWellDetails={selectedWell}
              imagedWells={expDashboardContext.imagedWells}
              isFocus={isFocus}
              wellKeyGenerator={wellKeyGenerator}
              setWellRef={setWellRef}
            />
          </div>
          <div
            id="welldashboard-bottommenu-container"
            className="absolute bottom-0 w-full"
          >
            {BottomMenu}
          </div>
        </div>
      )}
    </>
  );
};

export default wellDashboard;
